import {
  Alert,
  Anchor,
  Button,
  Container,
  Paper,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Disclaimer } from './Layout';
import { useDb } from './supabase';

type Step = { kind: 'email' } | { kind: 'code'; email: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * D1 Sign in: email → one-time code from the email (D5). Only existing office users get a code
 * (`shouldCreateUser: false`); people are invited by Admin (Task 5).
 */
export function SignInPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async (event?: FormEvent) => {
    event?.preventDefault();
    const address = email.trim().toLowerCase();
    if (!EMAIL.test(address)) {
      setError(t('signIn.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    const { error: failure } = await db.auth.signInWithOtp({
      email: address,
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    if (failure !== null) {
      setError(t('signIn.sendFailed', { message: failure.message }));
      return;
    }
    setCode('');
    setStep({ kind: 'code', email: address });
  };

  const verify = async (event?: FormEvent) => {
    event?.preventDefault();
    if (step.kind !== 'code') {
      return;
    }
    setBusy(true);
    setError(null);
    const { error: failure } = await db.auth.verifyOtp({
      email: step.email,
      token: code.trim(),
      type: 'email',
    });
    setBusy(false);
    if (failure !== null) {
      setError(t('signIn.codeFailed'));
    }
  };

  return (
    <Container size={460} py={60}>
      <Stack gap="lg">
        <Title order={1}>{t('signIn.title')}</Title>
        <Text>{t('signIn.purpose')}</Text>
        <Paper withBorder p="lg" radius="md">
          {step.kind === 'email' ? (
            <form onSubmit={(e) => void sendCode(e)}>
              <Stack>
                <TextInput
                  label={t('signIn.email')}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  size="md"
                />
                <Button type="submit" loading={busy} size="md">
                  {t('signIn.sendCode')}
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={(e) => void verify(e)}>
              <Stack>
                <Text>{t('signIn.codeSent', { email: step.email })}</Text>
                <Text fw={600} id="code-label">
                  {t('signIn.code')}
                </Text>
                <PinInput
                  length={6}
                  type="number"
                  oneTimeCode
                  value={code}
                  onChange={setCode}
                  size="lg"
                  aria-labelledby="code-label"
                  getInputProps={(index) => ({ 'aria-label': t('signIn.digit', { n: index + 1 }) })}
                />
                <Button type="submit" loading={busy} size="md" disabled={code.length < 6}>
                  {t('signIn.verify')}
                </Button>
                <Anchor component="button" type="button" onClick={() => void sendCode()}>
                  {t('signIn.sendAgain')}
                </Anchor>
                <Anchor
                  component="button"
                  type="button"
                  onClick={() => {
                    setStep({ kind: 'email' });
                    setError(null);
                  }}
                >
                  {t('signIn.otherEmail')}
                </Anchor>
              </Stack>
            </form>
          )}
        </Paper>
        {error === null ? null : (
          <Alert color="red" role="alert">
            {error}
          </Alert>
        )}
        <Disclaimer />
      </Stack>
    </Container>
  );
}
